import { node } from "~/util/dom";
import createSDSIcon from "~/ui/icon";
import { MYMAPS_FOLDER_MOVE, MYMAPS_FOLDER_REMOVE } from "~/ui/sds-compatible-icons";
import { ITrackListFolderItem } from "~/util/providers/mapybox";
import Tooltip, { ETooltipPosition } from "../tooltip";
import { _ } from "~/i18n";

type TClickType = "move" | "remove" | "folder";

interface IOpts {
	items: Array<ITrackListFolderItem>;
	clickCb?: (data: ITrackListFolderItem, type: TClickType) => void;
	onClose?: () => void;
}

function createItem(data: ITrackListFolderItem, onSignal: (type: TClickType) => void): HTMLLIElement {
	const el = node("li", { className: `contextmenu-item contextmenu-link-item` });
	const text = node("h3", { className: "name", textContent: data.title });
	const iconsElm = node("span", { className: "icons" });
	const iconMove = node("span", { className: "icon-move" });
	const iconRemove = node("span", { className: "icon-remove" });
	const folderMove = createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-folder-move", symbol: MYMAPS_FOLDER_MOVE });
	const folderRemove = createSDSIcon({ className: "sds-icon-mymaps-contextmemu sds-icon-folder-remove", symbol: MYMAPS_FOLDER_REMOVE });

	/* eslint-disable-next-line */
	new Tooltip({
		anchor: iconMove,
		content: node("span", {}, _("mymap.activity.moveToOtherFolder")),
		hideTimeout: 0,
		arrow: false,
		position: ETooltipPosition.TOP,
		offset: [2, 0, 0, 0],
	});
	/* eslint-disable-next-line */
	new Tooltip({
		anchor: iconRemove,
		content: node("span", {}, _("mymap.activity.removeFromFolder")),
		hideTimeout: 0,
		arrow: false,
		position: ETooltipPosition.TOP,
		offset: [2, 0, 0, 0],
	});

	el.addEventListener("click", event => {
		const target = event.target as HTMLElement;

		if (iconMove.contains(target)) {
			onSignal("move");
		} else if (iconRemove.contains(target)) {
			onSignal("remove");
		} else {
			onSignal("folder");
		}
	});
	iconMove.append(folderMove);
	iconRemove.append(folderRemove);
	iconsElm.append(iconMove, iconRemove);
	el.append(text, iconsElm);

	return el;
}

export default class BoxTracksContextMenu {
	_dom: {
		container: HTMLUListElement;
		[key: string]: HTMLElement;
	};

	_opened: boolean;

	_items: Array<{
		el: HTMLLIElement;
		data: ITrackListFolderItem;
	}>;

	_opts: IOpts;

	constructor(opts: IOpts) {
		this._opts = {
			items: [],
			clickCb: null,
			onClose: null,
			...opts,
		};
		this._dom = {
			container: node("ul", { className: "mymap-contextmenu tracks-contextmenu" }) as HTMLUListElement,
		};
		this._opened = false;
		this._items = [];

		this._create();
	}

	/**
	 * Ziskani obalu.
	 */
	get container() {
		return this._dom.container;
	}

	/**
	 * Je menu otevrene?
	 */
	isOpen() {
		return this._opened;
	}

	/**
	 * Otevreni - naplneni dat.
	 */
	open() {
		if (this._opened) {
			return;
		}

		// jinak se hnedka provedl handleEvent
		setTimeout(() => {
			document.addEventListener("click", this, false);
		}, 0);

		document.addEventListener("keydown", this);

		this._items.forEach(item => {
			this._dom.container.appendChild(item.el);
		});

		this._opened = true;
	}

	/**
	 * Zavreni - odepnuti dat.
	 */
	close() {
		if (!this._opened) {
			return;
		}

		document.removeEventListener("click", this, false);
		document.removeEventListener("keydown", this);

		this._items.forEach(item => {
			item.el.remove();
		});

		this._opened = false;
		this._opts.onClose && this._opts.onClose();
	}

	/**
	 * Prepinani zavreni/otevreni.
	 */
	toggle() {
		if (this._opened) {
			this.close();
		} else {
			this.open();
		}
	}

	handleEvent(event: Event) {
		if (!this._opened) {
			return;
		}

		switch (event.type) {
			case "click": {
				const target = (event as MouseEvent).target as HTMLLIElement;

				event.preventDefault();

				if (!this._dom.container.contains(target)) {
					this.close();
				}
				break;
			}

			case "keydown": {
				event.stopPropagation();
				event.preventDefault();

				const keyCode = (event as KeyboardEvent).which || (event as KeyboardEvent).keyCode;

				// esc
				if (keyCode === 27) {
					this.close();
				}
				break;
			}

			default:
		}
	}

	_create() {
		for (const data of this._opts.items) {
			this._items.push({
				el: createItem(data, type => {
					this._opts.clickCb(data, type);
				}),
				data,
			});
		}
	}
}
